import { Controller } from "@hotwired/stimulus"

// Controls the bottom resource panel in Plantafel
export default class extends Controller {
  static targets = [
    "content",
    "closeButton",
    "expandButton",
    "employeesContent",
    "employeesIcon",
    "vehiclesContent",
    "vehiclesIcon",
    "employeeList",
    "vehicleList",
    "monthEmployeeList",
    "monthVehicleList",
    "availableOnlyEmployeeCheckbox",
    "availableOnlyVehicleCheckbox",
    "weekAvailableOnlyEmployeeCheckbox",
    "weekAvailableOnlyVehicleCheckbox",
    "monthEmployeeSearch",
    "monthVehicleSearch",
  ]

  static values = {
    expanded: { type: Boolean, default: false },
  }

  static AVAILABLE_FILTER_KEY = "plantafel_resource_panel_available_only"
  static FILTER_CLASS = "filter-available-only"

  connect() {
    // Initialize state based on value
    if (this.expandedValue) {
      this.expand()
    } else {
      this.collapse()
    }
    // Apply initial calendar padding
    this.updateCalendarPadding(this.expandedValue)
    // Load available only filter state from localStorage
    this.loadAvailableOnlyFilterState()

    // The panel is position:fixed, so it doesn't follow the flex reflow when the
    // sidebar collapses/expands. Mirror the calendar container's measured left/width
    // onto the panel so it always spans exactly the visible board area (matches the
    // capacity-planning panel's behaviour).
    this.boundSchedule = this.scheduleSync.bind(this)
    window.addEventListener("resize", this.boundSchedule)
    if (window.ResizeObserver) {
      const container = document.getElementById("plantafel_calendar_container")
      if (container) {
        this.widthObserver = new ResizeObserver(this.boundSchedule)
        this.widthObserver.observe(container)
      }
    }
    this.syncAlignment()
  }

  disconnect() {
    window.removeEventListener("resize", this.boundSchedule)
    this.widthObserver?.disconnect()
  }

  // Coalesce multiple observer/resize ticks into one alignment pass per frame.
  scheduleSync() {
    if (this.syncScheduled) return
    this.syncScheduled = true
    requestAnimationFrame(() => {
      this.syncScheduled = false
      this.syncAlignment()
    })
  }

  // Snap the fixed panel's left edge and width to the calendar container, so it
  // tracks the sidebar collapse/expand (the container is a flex sibling that resizes).
  syncAlignment() {
    const container = document.getElementById("plantafel_calendar_container")
    if (!container) return
    const rect = container.getBoundingClientRect()
    this.element.style.left = `${rect.left}px`
    this.element.style.right = "auto"
    this.element.style.width = `${rect.width}px`
  }

  toggle(_event) {
    this.expandedValue = !this.expandedValue

    if (this.expandedValue) {
      this.expand()
    } else {
      this.collapse()
    }
  }

  expand() {
    if (this.hasContentTarget) {
      this.contentTarget.style.maxHeight = "50vh"
    }
    this.expandedValue = true
    this.updateToggleButtons()
    this.updateCalendarPadding(true)
  }

  collapse() {
    if (this.hasContentTarget) {
      this.contentTarget.style.maxHeight = "0"
    }
    this.expandedValue = false
    this.updateToggleButtons()
    this.updateCalendarPadding(false)
  }

  updateCalendarPadding(expanded) {
    // Find the spacer element (defined in main_component.html.erb)
    const spacer = document.getElementById("plantafel_bottom_spacer")
    if (spacer) {
      if (expanded) {
        // Add spacer height to account for expanded panel (50vh + header ~44px)
        spacer.style.height = "calc(50vh + 60px)"
      } else {
        // Smaller spacer when collapsed (just enough for the header bar)
        spacer.style.height = "60px"
      }
    }
  }

  updateToggleButtons() {
    if (this.hasExpandButtonTarget) {
      this.expandButtonTarget.classList.toggle("hidden", this.expandedValue)
    }
    if (this.hasCloseButtonTarget) {
      this.closeButtonTarget.classList.toggle("hidden", !this.expandedValue)
    }
  }

  close(event) {
    if (event) {
      event.stopPropagation()
      event.preventDefault()
    }
    this.collapse()
  }

  // Toggle employee/vehicle sections
  toggleSection(event) {
    const section = event.currentTarget.dataset.section

    if (section === "employees") {
      this.toggleEmployeesSection()
    } else if (section === "vehicles") {
      this.toggleVehiclesSection()
    }
  }

  toggleEmployeesSection() {
    if (this.hasEmployeesContentTarget) {
      this.employeesContentTarget.classList.toggle("hidden")
    }
    if (this.hasEmployeesIconTarget) {
      this.employeesIconTarget.classList.toggle("rotate-180")
    }
  }

  toggleVehiclesSection() {
    if (this.hasVehiclesContentTarget) {
      this.vehiclesContentTarget.classList.toggle("hidden")
    }
    if (this.hasVehiclesIconTarget) {
      // Toggle between right arrow (collapsed) and down arrow (expanded)
      const isCollapsed = this.vehiclesContentTarget.classList.contains("hidden")
      if (isCollapsed) {
        this.vehiclesIconTarget.innerHTML =
          '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>'
      } else {
        this.vehiclesIconTarget.innerHTML =
          '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>'
      }
    }
  }

  // Filter employees by search input (name, job, or trade)
  // Availability filtering is handled by CSS via filter-available-only class
  filterEmployees(event) {
    const searchTerm = event.target.value.toLowerCase()
    const date = event.target.dataset.date

    // Find the employee list for this date
    const employeeList = this.employeeListTargets.find((list) => list.dataset.date === date)

    if (employeeList) {
      const items = employeeList.querySelectorAll('[data-resource-type="Employee"]')
      items.forEach((item) => {
        const name = item.dataset.resourceName?.toLowerCase() || ""
        const job = item.dataset.employeeJob?.toLowerCase() || ""
        const trades = item.dataset.employeeTrades?.toLowerCase() || ""

        const matchesSearch =
          name.includes(searchTerm) || job.includes(searchTerm) || trades.includes(searchTerm)

        // Only set display for search filtering; CSS handles availability filtering
        item.style.display = matchesSearch ? "" : "none"
      })
    }
  }

  // Filter vehicles by search input
  // Availability filtering is handled by CSS via filter-available-only class
  filterVehicles(event) {
    const searchTerm = event.target.value.toLowerCase()
    const date = event.target.dataset.date

    // Find the vehicle list for this date
    const vehicleList = this.vehicleListTargets.find((list) => list.dataset.date === date)

    if (vehicleList) {
      const items = vehicleList.querySelectorAll('[data-resource-type="Fleet"]')
      items.forEach((item) => {
        const name = item.dataset.resourceName?.toLowerCase() || ""
        const type = item.querySelector(".text-gray-500")?.textContent?.toLowerCase() || ""

        const matchesSearch = name.includes(searchTerm) || type.includes(searchTerm)

        // Only set display for search filtering; CSS handles availability filtering
        item.style.display = matchesSearch ? "" : "none"
      })
    }
  }

  // Filter employees in month view (single list, no date column) - searches name, job, or trade
  // Availability filtering is handled by CSS via filter-available-only class
  filterMonthEmployees(event) {
    const searchTerm = event.target.value.toLowerCase()

    if (this.hasMonthEmployeeListTarget) {
      const items = this.monthEmployeeListTarget.querySelectorAll('[data-resource-type="Employee"]')
      items.forEach((item) => {
        const name = item.dataset.resourceName?.toLowerCase() || ""
        const job = item.dataset.employeeJob?.toLowerCase() || ""
        const trades = item.dataset.employeeTrades?.toLowerCase() || ""

        const matchesSearch =
          name.includes(searchTerm) || job.includes(searchTerm) || trades.includes(searchTerm)

        // Only set display for search filtering; CSS handles availability filtering
        item.style.display = matchesSearch ? "" : "none"
      })
    }
  }

  // Filter vehicles in month view (single list, no date column)
  // Availability filtering is handled by CSS via filter-available-only class
  filterMonthVehicles(event) {
    const searchTerm = event.target.value.toLowerCase()

    if (this.hasMonthVehicleListTarget) {
      const items = this.monthVehicleListTarget.querySelectorAll('[data-resource-type="Fleet"]')
      items.forEach((item) => {
        const name = item.dataset.resourceName?.toLowerCase() || ""
        const type = item.querySelector(".text-gray-500")?.textContent?.toLowerCase() || ""

        const matchesSearch = name.includes(searchTerm) || type.includes(searchTerm)

        // Only set display for search filtering; CSS handles availability filtering
        item.style.display = matchesSearch ? "" : "none"
      })
    }
  }

  // Load available only filter state from localStorage and apply CSS classes
  loadAvailableOnlyFilterState() {
    const state = this.getAvailableOnlyFilterState()

    // Month view checkboxes
    if (this.hasAvailableOnlyEmployeeCheckboxTarget) {
      this.availableOnlyEmployeeCheckboxTarget.checked = state.employee
      this.applyAvailableOnlyFilter("employee", state.employee)
    }

    if (this.hasAvailableOnlyVehicleCheckboxTarget) {
      this.availableOnlyVehicleCheckboxTarget.checked = state.vehicle
      this.applyAvailableOnlyFilter("vehicle", state.vehicle)
    }

    // Week view checkboxes
    if (this.hasWeekAvailableOnlyEmployeeCheckboxTarget) {
      this.weekAvailableOnlyEmployeeCheckboxTarget.checked = state.employee
      this.applyWeekAvailableOnlyFilter("employee", state.employee)
    }

    if (this.hasWeekAvailableOnlyVehicleCheckboxTarget) {
      this.weekAvailableOnlyVehicleCheckboxTarget.checked = state.vehicle
      this.applyWeekAvailableOnlyFilter("vehicle", state.vehicle)
    }
  }

  // Get available only filter state from localStorage
  getAvailableOnlyFilterState() {
    try {
      const stored = localStorage.getItem(this.constructor.AVAILABLE_FILTER_KEY)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (e) {
      console.warn("Error reading available filter state from localStorage:", e)
    }
    return { employee: false, vehicle: false }
  }

  // Save available only filter state to localStorage
  saveAvailableOnlyFilterState(state) {
    try {
      localStorage.setItem(this.constructor.AVAILABLE_FILTER_KEY, JSON.stringify(state))
    } catch (e) {
      console.warn("Error saving available filter state to localStorage:", e)
    }
  }

  // Handle checkbox change for month view available only filter
  toggleAvailableOnlyFilter(event) {
    const checkbox = event.target
    const resourceType = checkbox.dataset.resourceType
    const showOnlyAvailable = checkbox.checked

    // Save state to localStorage
    const state = this.getAvailableOnlyFilterState()
    state[resourceType] = showOnlyAvailable
    this.saveAvailableOnlyFilterState(state)

    // Apply the filter via CSS class
    this.applyAvailableOnlyFilter(resourceType, showOnlyAvailable)
  }

  // Apply available only filter to month view list via CSS class
  applyAvailableOnlyFilter(resourceType, showOnlyAvailable) {
    if (resourceType === "employee" && this.hasMonthEmployeeListTarget) {
      this.monthEmployeeListTarget.classList.toggle(
        this.constructor.FILTER_CLASS,
        showOnlyAvailable,
      )
    } else if (resourceType === "vehicle" && this.hasMonthVehicleListTarget) {
      this.monthVehicleListTarget.classList.toggle(this.constructor.FILTER_CLASS, showOnlyAvailable)
    }
  }

  // Handle checkbox change for week view available only filter (global)
  toggleWeekAvailableOnlyFilter(event) {
    const checkbox = event.target
    const resourceType = checkbox.dataset.resourceType
    const showOnlyAvailable = checkbox.checked

    // Save state to localStorage (shared with month view)
    const state = this.getAvailableOnlyFilterState()
    state[resourceType] = showOnlyAvailable
    this.saveAvailableOnlyFilterState(state)

    // Apply the filter to all date columns via CSS class
    this.applyWeekAvailableOnlyFilter(resourceType, showOnlyAvailable)
  }

  // Apply available only filter to all date columns in week view via CSS class
  applyWeekAvailableOnlyFilter(resourceType, showOnlyAvailable) {
    if (resourceType === "employee") {
      this.employeeListTargets.forEach((list) => {
        list.classList.toggle(this.constructor.FILTER_CLASS, showOnlyAvailable)
      })
    } else if (resourceType === "vehicle") {
      this.vehicleListTargets.forEach((list) => {
        list.classList.toggle(this.constructor.FILTER_CLASS, showOnlyAvailable)
      })
    }
  }
}
